import { useMemo, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { Icone } from '../Icone';
import {
  ENTETES_SOUS_PROFIL,
  LIBELLE_MARQUE,
  SIDEBAR,
  sectionsDuFormateur,
  type EntreeNavigation,
} from '../../config/sidebar';
import { useAuth } from '../../auth/useAuth';
import { useConversations } from '../../lib/useMessagerie';
import { useDevoirsApprenant, useMesDocumentsApprenant } from '../../lib/useEspaceApprenant';
import { useCatalogue } from '../../lib/useCatalogue';
import { useRuptures } from '../../lib/useRuptures';
import { useAlertesOuvertes } from '../../lib/useAlertes';
import { useFichesATraiter } from '../../lib/useEspaceFormateur';
import { usePerimetre } from '../../site/usePerimetre';
import { LogoMarque } from '../../marque/LogoMarque';
import { useMarque } from '../../marque/useMarque';
import { initialesDuNom } from '../../marque/marque';
import { useEcranEtroit } from '../../lib/useEcranEtroit';
import { usePiegeFocus } from '../../lib/usePiegeFocus';

const TONS_PASTILLE = {
  mint: 'badge-mint',
  warning: 'badge-warning',
  info: 'badge-info',
  danger: 'badge-danger',
} as const;

interface Props {
  ouverte: boolean;
  onFermer: () => void;
}

export function Sidebar({ ouverte, onFermer }: Props): JSX.Element | null {
  const { utilisateur, peutAcceder, deconnexion } = useAuth();
  const estApprenant = utilisateur?.profil === 'APPRENANT';
  const estAdministratif = utilisateur?.profil === 'ADMINISTRATIF';
  const { site } = usePerimetre();
  const { marque } = useMarque();
  const { data: dataMessagerie } = useConversations();
  const { data: dataDevoirs } = useDevoirsApprenant(estApprenant);
  const { data: dataDocuments } = useMesDocumentsApprenant(estApprenant);
  const { data: dataCatalogue } = useCatalogue(site, estApprenant);
  const { data: dataRuptures } = useRuptures(site, estAdministratif);
  const { data: dataAlertes } = useAlertesOuvertes(site, peutAcceder('alertes'));
  // Fiches séquence : ses actions pour le formateur, les fiches à valider pour
  // le pôle pédagogique et la direction (CDC §7.3).
  const suitFiches =
    ['FORMATEUR', 'PEDAGOGIQUE', 'DIRECTION'].includes(utilisateur?.profil ?? '') &&
    peutAcceder('form-fiches');
  const { data: dataFiches } = useFichesATraiter(suitFiches);
  // Sous `lg`, le menu est un tiroir par-dessus l'écran : ouvert, c'est une
  // fenêtre au clavier (focus sur « Fermer », `Échap`, tabulation enfermée) ;
  // fermé, il sort de la tabulation (`invisible` après le glissement), sans
  // quoi le focus partait sur des liens hors de l'écran (TF-341).
  const tiroir = useEcranEtroit(1024);
  const panneau = useRef<HTMLElement>(null);
  usePiegeFocus(panneau, tiroir && ouverte, onFermer, { focusInitial: 'premier' });

  const totalMessagesNonLus = useMemo(() => {
    if (!dataMessagerie?.conversations) return null;
    return dataMessagerie.conversations.reduce((tot, c) => tot + (c.nonLus || 0), 0);
  }, [dataMessagerie]);

  const totalDevoirsARendre = dataDevoirs?.aRendre?.length;
  const totalPiecesAFournir = dataDocuments?.piecesAFournir;
  const totalFormationsCatalogue = dataCatalogue?.compteurs?.formations;
  const totalRuptures = dataRuptures?.compteurs?.ruptures;

  if (!utilisateur) return null;

  const configuration = SIDEBAR[utilisateur.profil];
  const entete = utilisateur.sousProfil ? ENTETES_SOUS_PROFIL[utilisateur.sousProfil] : null;
  // Le libellé de l'organisme suit la marque blanche (TF-436).
  const libelle = (texte: string): string => (texte === LIBELLE_MARQUE ? marque.nom : texte);
  const initiales =
    entete?.initiales ??
    (configuration.titre === LIBELLE_MARQUE ? initialesDuNom(marque.nom) : configuration.initiales);

  function resoudrePastille(entree: EntreeNavigation) {
    if (entree.ecran === 'messages' && totalMessagesNonLus !== null) {
      if (totalMessagesNonLus > 0) {
        return { valeur: String(totalMessagesNonLus), ton: 'info' as const };
      }
      return null;
    }
    // Les alertes ouvertes de la page, jamais un chiffre de maquette pendant le
    // chargement ni les notifications non lues de la cloche (TF-492).
    if (entree.ecran === 'alertes') {
      const ouvertes = dataAlertes?.ouvertes ?? 0;
      return ouvertes > 0 ? { valeur: String(ouvertes), ton: 'warning' as const } : null;
    }
    if (entree.ecran === 'assignments' && totalDevoirsARendre !== undefined) {
      if (totalDevoirsARendre > 0) {
        return { valeur: String(totalDevoirsARendre), ton: 'warning' as const };
      }
      return null;
    }
    if (entree.ecran === 'app-documents' && totalPiecesAFournir !== undefined) {
      if (totalPiecesAFournir > 0) {
        return { valeur: String(totalPiecesAFournir), ton: 'warning' as const };
      }
      return null;
    }
    if (entree.ecran === 'catalog' && estApprenant) {
      if (totalFormationsCatalogue !== undefined) {
        return { valeur: String(totalFormationsCatalogue), ton: 'mint' as const };
      }
      return null;
    }
    if (entree.ecran === 'form-fiches') {
      if (!dataFiches || dataFiches.nombre === 0) return null;
      return {
        valeur: String(dataFiches.nombre),
        ton: dataFiches.rappel?.bloque ? ('danger' as const) : ('warning' as const),
      };
    }
    if (entree.ecran === 'adm-ruptures' && totalRuptures !== undefined) {
      if (totalRuptures > 0) {
        return { valeur: String(totalRuptures), ton: 'danger' as const };
      }
      return null;
    }
    return entree.pastille ?? null;
  }

  // Les rubriques hors périmètre ne sont pas affichées ; l'API les refuse de
  // toute façon si le lien est forgé à la main.
  const sections = sectionsDuFormateur(configuration.sections, utilisateur.statutFormateur)
    .map((section) => ({
      ...section,
      entrees: section.entrees.filter((entree) => peutAcceder(entree.ecran)),
    }))
    .filter((section) => section.entrees.length > 0);

  return (
    <>
      {ouverte && (
        <div
          className="fixed inset-0 z-40 bg-codialis-navy/40 lg:hidden"
          onClick={onFermer}
          aria-hidden="true"
        />
      )}

      <aside
        ref={panneau}
        aria-label="Menu"
        {...(tiroir && ouverte ? { role: 'dialog', 'aria-modal': true } : {})}
        // Visible dès l'ouverture, pour recevoir le focus ; masqué seulement au
        // bout du glissement de fermeture (la visibilité suit la transition).
        className={`fixed inset-y-0 left-0 z-50 flex h-screen w-64 shrink-0 flex-col border-r border-gray-200 bg-white lg:visible lg:sticky lg:top-0 lg:translate-x-0 ${
          ouverte ? 'visible translate-x-0 transition-transform' : 'invisible -translate-x-full transition-[transform,visibility]'
        }`}
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-5">
          <LogoMarque className="h-7 w-auto" />
          <button
            type="button"
            className="-mr-2 rounded-lg p-2.5 text-codialis-gray transition hover:bg-gray-100 lg:hidden"
            onClick={onFermer}
            aria-label="Fermer la navigation"
          >
            <svg aria-hidden="true" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Vignette de l'espace de travail */}
        <div className="border-b border-gray-100 px-4 py-3">
          <div className="flex items-center gap-3 rounded-xl bg-codialis-sand p-3">
            <div
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ${configuration.accent} text-xs font-bold text-white`}
            >
              {initiales}
            </div>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-codialis-navy">
                {entete?.titre ?? libelle(configuration.titre)}
              </div>
              <div className="truncate text-[11px] text-codialis-gray">
                {utilisateur.sites.length === 1
                  ? `Antenne ${utilisateur.sites[0]?.code}`
                  : libelle(configuration.sousTitre)}
              </div>
            </div>
          </div>
        </div>

        <nav aria-label="Navigation principale" className="scrollbar-thin min-w-0 flex-1 space-y-6 overflow-y-auto px-3 py-4">
          {sections.map((section, rang) => (
            <div key={section.titre} role="group" aria-labelledby={`menu-section-${rang}`}>
              <div
                id={`menu-section-${rang}`}
                className="mb-2 px-3 text-[10px] font-bold uppercase tracking-widest text-codialis-gray"
              >
                {section.titre}
              </div>
              {section.entrees.map((entree) => (
                <NavLink
                  key={entree.ecran}
                  to={`/${entree.ecran}`}
                  onClick={onFermer}
                  className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                >
                  <Icone nom={entree.icone} className="nav-icon h-4 w-4 text-codialis-gray" />
                  <span className="truncate">{entree.libelle}</span>
                  {(() => {
                    const pastille = resoudrePastille(entree);
                    if (pastille) {
                      return (
                        <span className={`badge ml-auto ${TONS_PASTILLE[pastille.ton]}`}>
                          {pastille.valeur}
                        </span>
                      );
                    }
                    if (entree.etiquette) {
                      return (
                        <span className="gradient-mint ml-auto rounded px-1.5 py-0.5 text-[9px] font-semibold text-white">
                          {entree.etiquette}
                        </span>
                      );
                    }
                    return null;
                  })()}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {/* Place du bouton flottant d'accessibilité (TF-491) : dès `lg`, il se
            pose dans cette bande, au-dessus du bloc utilisateur de hauteur
            fixe (`h-20`, voir `PanneauAccessibilite`). Le menu qui défile
            s'arrête au-dessus : aucune entrée ne passe sous le bouton. */}
        {peutAcceder('accessibility') && <div aria-hidden="true" className="hidden h-14 shrink-0 lg:block" />}

        <div className="flex h-20 shrink-0 items-center border-t border-gray-100 px-3">
          <div className="flex min-w-0 flex-1 items-center gap-3 px-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-codialis-sky to-codialis-blue text-sm font-semibold text-white">
              {utilisateur.initiales}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-codialis-navy">
                {utilisateur.prenom} {utilisateur.nom}
              </div>
              <div className="truncate text-[11px] text-codialis-gray">
                {utilisateur.libelleProfil}
              </div>
            </div>
            <button
              type="button"
              onClick={() => void deconnexion()}
              title="Se déconnecter"
              aria-label="Se déconnecter"
              className="shrink-0 rounded-lg p-2.5 text-codialis-gray transition hover:bg-gray-100 hover:text-codialis-navy"
            >
              <svg aria-hidden="true" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
